/**
 * Work order CRUD & core reads.
 *
 * Extracted verbatim from workOrdersService.ts — functions are invoked with
 * `this` bound to the WorkOrdersService instance (WorkOrdersCtx).
 */
import { extractItems } from '../../utils/apiHelpers';
/**
 * Get account work orders
 */
export async function getAccountWorkOrders(accountId, opts) {
    try {
        const params = {};
        if (opts?.limit != null)
            params.limit = opts.limit;
        if (opts?.offset != null)
            params.offset = opts.offset;
        if (opts?.status)
            params.status = opts.status;
        if (opts?.sort_by)
            params.sort_by = opts.sort_by;
        if (opts?.sort_desc != null)
            params.sort_desc = opts.sort_desc;
        if (opts?.department_ids)
            params.department_ids = opts.department_ids;
        if (opts?.search)
            params.search = opts.search;
        if (opts?.tag_ids) {
            const tagValue = Array.isArray(opts.tag_ids) ? opts.tag_ids.filter(Boolean).join(',') : opts.tag_ids;
            if (tagValue)
                params.tag_ids = tagValue;
        }
        if (opts?.include_deleted)
            params.include_deleted = true;
        if (opts?.past_due)
            params.past_due = true;
        if (opts?.customer_id)
            params.customer_id = opts.customer_id;
        const response = await this.client.get(`/accounts/${accountId}/work-orders`, params);
        if (!response.success || !response.data) {
            throw new Error(response.error || 'Failed to fetch account work orders');
        }
        return this.buildListResponse(response.data, opts);
    }
    catch (error) {
        console.error('[WorkOrdersService] Get account work orders error:', error);
        throw error;
    }
}
/**
 * Get location work orders
 */
export async function getLocationWorkOrders(locationId, opts) {
    try {
        const params = {};
        if (opts?.limit != null)
            params.limit = opts.limit;
        if (opts?.offset != null)
            params.offset = opts.offset;
        if (opts?.status)
            params.status = opts.status;
        if (opts?.sort_by)
            params.sort_by = opts.sort_by;
        if (opts?.sort_desc != null)
            params.sort_desc = opts.sort_desc;
        if (opts?.department_ids)
            params.department_ids = opts.department_ids;
        if (opts?.search)
            params.search = opts.search;
        if (opts?.tag_ids) {
            const tagValue = Array.isArray(opts.tag_ids) ? opts.tag_ids.filter(Boolean).join(',') : opts.tag_ids;
            if (tagValue)
                params.tag_ids = tagValue;
        }
        if (opts?.include_deleted)
            params.include_deleted = true;
        if (opts?.closed_today_only)
            params.closed_today_only = true;
        if (opts?.past_due)
            params.past_due = true;
        if (opts?.customer_id)
            params.customer_id = opts.customer_id;
        const response = await this.client.get(`/locations/${locationId}/work-orders`, params);
        if (!response.success || !response.data) {
            throw new Error(response.error || 'Failed to fetch location work orders');
        }
        return this.buildListResponse(response.data, opts);
    }
    catch (error) {
        console.error('[WorkOrdersService] Get location work orders error:', error);
        throw error;
    }
}
/**
 * Get work order by ID
 * @param id - Work order ID
 * @param bustCache - Force cache bust
 * @param options - Additional options
 * @param options.hideDeniedServices - When true, hide denied services (used after invoice generation/work order closed)
 */
export async function getWorkOrder(id, bustCache = false, options) {
    try {
        const params = {};
        if (bustCache) {
            params._t = Date.now();
        }
        if (options?.hideDeniedServices) {
            params.hide_denied_services = true;
        }
        const response = await this.client.get(`/work-orders/${id}`, Object.keys(params).length > 0 ? params : undefined);
        if (!response.success || !response.data) {
            // Keep the status and body: callers act on codes (OTHER_ACCOUNT,
            // LOCATION_ACCESS_DENIED) that the message alone does not carry.
            throw Object.assign(new Error(response.error || 'Failed to fetch work order'), {
                status: response.status,
                errorBody: response.errorBody,
            });
        }
        const o = response.data.work_order || response.data.data || response.data;
        // Get base work order data
        const base = this.normalizeWorkOrder(o);
        const detail = {
            ...base,
            name: base.name ?? o.name ?? o.title ?? undefined,
            workflow_name: base.workflow_name ?? o.workflow?.name ?? o.workflow_name ?? undefined,
            notes: o.notes ?? undefined,
            // Work order level fields from API
            hat_number: o.hat_number ?? undefined,
            ro_number: o.ro_number ?? undefined,
            po_number: o.po_number ?? undefined,
            // Active department ID (UUID) for determining current workflow step
            active_department_id: o.active_department_id ?? undefined,
            // Additional fields from new API
            department_section_id: o.department_section_id ?? undefined,
            department_section_name: o.department_section_name ?? undefined,
            department_name: o.department_name ?? base.current_department?.name ?? undefined,
            priority: o.priority ?? undefined,
            work_order_type: o.work_order_type ?? undefined,
            // New nested sections structure (pre-sorted by backend)
            sections: Array.isArray(o.sections) ? o.sections : undefined,
            // Partner back-reference (server-resolved from the linked partner_jobs
            // row). normalizeWorkOrder is a strict allowlist and drops these, so the
            // detail builder must copy them through explicitly — otherwise the
            // partner name / external job id / deep-link never reach the header on
            // web or mobile, however correct the API response is.
            partner_external_job_id: o.partner_external_job_id ?? undefined,
            partner_name: o.partner_name ?? undefined,
            partner_job_url: o.partner_job_url ?? undefined,
            // Lines waiting on a partner change request, and who each waits for —
            // the work order's "Waiting for …" markers and the dealership buttons.
            // Same allowlist rule: copied through or they never reach the screen.
            partner_pending_service_ids: Array.isArray(o.partner_pending_service_ids) ? o.partner_pending_service_ids : undefined,
            partner_pending_approvals: Array.isArray(o.partner_pending_approvals) ? o.partner_pending_approvals : undefined,
        };
        return detail;
    }
    catch (error) {
        console.error('[WorkOrdersService] Get work order error:', error);
        throw error;
    }
}
/**
 * Create work order
 */
export async function createWorkOrder(locationId, body) {
    try {
        const response = await this.client.post(`/locations/${locationId}/work-orders`, body);
        if (!response.success || !response.data) {
            // Preserve the parsed error body: a create blocked by a service.add
            // rule (e.g. prior_service_within) carries a structured
            // `{declines, can_override}` envelope the create wizard parses
            // (parseGuardDeclines) to offer a manager-override retry.
            const err = new Error(response.error || 'Failed to create work order');
            if (response.errorBody)
                err.errorBody = response.errorBody;
            throw err;
        }
        const wo = response.data.work_order || response.data;
        return this.normalizeWorkOrder(wo);
    }
    catch (error) {
        console.error('[WorkOrdersService] Create work order error:', error);
        throw error;
    }
}
/**
 * Update work order
 */
export async function updateWorkOrder(workOrderId, body) {
    try {
        const response = await this.client.put(`/work-orders/${workOrderId}`, body);
        if (!response.success || !response.data) {
            throw new Error(response.error || 'Failed to update work order');
        }
        const wo = response.data.work_order || response.data;
        return this.normalizeWorkOrder(wo);
    }
    catch (error) {
        console.error('[WorkOrdersService] Update work order error:', error);
        throw error;
    }
}
/**
 * Update work order vehicle
 */
export async function updateWorkOrderVehicle(workOrderId, data) {
    try {
        const response = await this.client.put(`/work-orders/${workOrderId}/vehicle`, data);
        if (!response.success) {
            throw new Error(response.error || 'Failed to update work order vehicle');
        }
    }
    catch (error) {
        console.error('[WorkOrdersService] Update work order vehicle error:', error);
        throw error;
    }
}
/**
 * Set the thumbnail for a work order
 * @param workOrderId - The work order ID
 * @param attachmentKey - The file key to set as thumbnail
 * @returns Updated work order detail
 */
export async function setThumbnail(workOrderId, attachmentKey) {
    try {
        const response = await this.client.put(`/work-orders/${workOrderId}/thumbnail`, {
            attachment_key: attachmentKey,
        });
        if (!response.success || !response.data) {
            throw new Error(response.error || 'Failed to set thumbnail');
        }
        const wo = response.data.work_order || response.data;
        return this.normalizeWorkOrder(wo);
    }
    catch (error) {
        console.error('[WorkOrdersService] Set thumbnail error:', error);
        throw error;
    }
}
/**
 * Clear the thumbnail for a work order
 * @param workOrderId - The work order ID
 * @returns Updated work order detail
 */
export async function clearThumbnail(workOrderId) {
    try {
        const response = await this.client.delete(`/work-orders/${workOrderId}/thumbnail`);
        if (!response.success) {
            throw new Error(response.error || 'Failed to clear thumbnail');
        }
        // The response may or may not contain the work order depending on implementation
        // Fetch fresh data to ensure consistency
        return this.getWorkOrder(workOrderId);
    }
    catch (error) {
        console.error('[WorkOrdersService] Clear thumbnail error:', error);
        throw error;
    }
}
// Export - API client auto-detects blob by Content-Type header
export async function exportWorkOrders(locationId, format, filters) {
    try {
        const params = { format };
        if (filters?.status)
            params.status = filters.status;
        if (filters?.department_ids)
            params.department_ids = filters.department_ids;
        if (filters?.tag_ids)
            params.tag_ids = filters.tag_ids;
        if (filters?.past_due)
            params.past_due = 'true';
        const response = await this.client.get(`/locations/${locationId}/work-orders/export`, params);
        if (!response.success || !response.data) {
            throw new Error(response.error || 'Failed to export work orders');
        }
        return response.data;
    }
    catch (error) {
        console.error('[WorkOrdersService] Export work orders error:', error);
        throw error;
    }
}
export async function getActivitiesByResourceId(locationId, resourceId, limit = 100) {
    try {
        const params = {
            resource_id: resourceId,
            limit,
        };
        const response = await this.client.get(`/locations/${locationId}/activities`, params);
        if (!response.success || !response.data) {
            throw new Error(response.error || 'Failed to fetch activities');
        }
        const data = response.data;
        const activities = extractItems(data, ['activities', 'items', 'data']);
        return {
            activities,
            total: data.total || data.pagination?.total || activities.length,
            limit: data.limit,
            offset: data.offset,
        };
    }
    catch (error) {
        console.error('[WorkOrdersService] Get activities by resource ID error:', error);
        throw error;
    }
}
/**
 * Get pricing permissions for a work order
 * Returns whether the current user can edit pricing/payroll fields based on:
 * - Work order stage (pre_approved, inspection, approval, production, closed)
 * - User's stage-specific pricing permissions
 * - For closed work orders: whether within 30-day edit window
 */
export async function getPricingPermissions(workOrderId) {
    try {
        const response = await this.client.get(`/work-orders/${workOrderId}/pricing-permissions`);
        if (!response.success || !response.data) {
            console.warn('[WorkOrdersService] Failed to get pricing permissions:', response.error);
            return null;
        }
        return response.data;
    }
    catch (error) {
        console.error('[WorkOrdersService] Get pricing permissions error:', error);
        return null;
    }
}
/**
 * Get available work order types for a location
 * Returns types with availability status based on location config and user permissions
 */
export async function getAvailableWorkOrderTypes(locationId) {
    try {
        const response = await this.client.get(`/locations/${locationId}/work-order-types`);
        if (!response.success) {
            throw new Error(response.error || 'Failed to load available work order types');
        }
        return response.data ?? { available_types: [] };
    }
    catch (error) {
        console.error('[WorkOrdersService] Get available work order types error:', error);
        throw error;
    }
}
